import { useCallback, useMemo } from "react";

import { IconPlus, IconRefresh } from "@tabler/icons";
import { useTheme } from "styled-components";

import { useGetLicencesQuery } from "@sso/apis/licences/hooks";
import { TableHeader } from "@sso/components/TableHeader";
import { TableLayoutWrapper } from "@sso/components/TableLayoutWrapper";
import { TablePagination } from "@sso/components/TablePagination";
import { Dialog, UserRole } from "@sso/constant";
import { TableProvider, TableProviderState } from "@sso/context/tableContext";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { openDialog } from "@sso/store/slices/dialog";
import { selectLicencePagination } from "@sso/store/slices/licence";
import { LicenceSummaryKeys } from "@sso/types";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";

import { LicencesTable } from "./licencesTable";
import { licenceTableColumns } from "./licenceTableColumns";

const dataKeys = [
  LicenceSummaryKeys.SOFTWARE_NAME,
  LicenceSummaryKeys.LICENCE_NAME,
  LicenceSummaryKeys.ORGANISATION_NAME,
  LicenceSummaryKeys.CREATE_DATE_LABEL,
  LicenceSummaryKeys.UPDATE_DATE_LABEL,
  LicenceSummaryKeys.EXPIRY_DATE_LABEL,
  LicenceSummaryKeys.TYPE_NAME,
  LicenceSummaryKeys.CONCURRENT_USERS_OR_USERNAME,
];

const Licences = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);

  const licencingPagination = useAppSelector(selectLicencePagination);
  const { data, isFetching, error, refetch } = useGetLicencesQuery();

  const handleAddLicence = useCallback(() => dispatch(openDialog({ type: Dialog.ADD_LICENCE })), [dispatch]);

  const licenceModifiedTableColumns = useMemo(() => {
    let columns = [...licenceTableColumns];
    if (user?.role.roleId !== UserRole.ADMINISTRATOR) {
      columns = columns.filter(item => item.name !== "Organisation");
    }
    return columns;
  }, [user?.role.roleId]);

  const licenceModifiedDataKeys = useMemo(() => {
    let dks = [...dataKeys];
    if (user?.role.roleId !== UserRole.ADMINISTRATOR) {
      dks = dks.filter(item => item !== LicenceSummaryKeys.ORGANISATION_NAME);
    }
    return dks;
  }, [user?.role.roleId]);

  const tableContextInstance: TableProviderState = { ...licencingPagination, columns: licenceModifiedTableColumns };

  const TableActions = (
    <Flex>
      <IconButton title="Refresh" onClick={refetch}>
        <IconRefresh />
      </IconButton>
      {user?.role.roleId === UserRole.ADMINISTRATOR && (
        <IconButton title="Add Licence" onClick={handleAddLicence}>
          <IconPlus />
        </IconButton>
      )}
    </Flex>
  );

  return (
    <>
      {isFetching && <CircleLoading color={theme.colors.secondary.dark} size={8} blurBackground />}
      <TableProvider tableInstance={tableContextInstance}>
        <TableHeader title="Licences" TableActions={TableActions} />
        <TableLayoutWrapper error={error} data={data}>
          {data && (
            <LicencesTable
              data={data.resources}
              licenceTableColumns={licenceModifiedTableColumns}
              dataKeys={licenceModifiedDataKeys}
            />
          )}
        </TableLayoutWrapper>
        <TablePagination totalPages={data?.totalPages} />
      </TableProvider>
    </>
  );
};

export default Licences;
